/**
 * The substance catalogue, from the one place it now lives.
 *
 * This used to be a display-only mirror of the backend's, which is why adding a
 * single reagent needed a network round trip to say what it was. The whole
 * record — litmus, pH and acid-base category included — is in
 * `@padcanvas/shared`, so a single-substance test is answered here.
 *
 * Re-exported through this module rather than imported directly by every
 * component, so the bench has one place to look and a future move costs one file.
 * The labels, ordering and gas mapping below are presentation, and stay here.
 */
export type {
    Substance, SubstanceCategory, PhysicalState, AcidBaseCategory, SolidForm,
    DoseUnit, Dose, LiquidFlow,
} from '@padcanvas/shared';
export {
    SUBSTANCE_CATALOG, COLOURLESS, findSubstance, intrinsicOutcomeFor, pHToBand,
    DROP_ML, perAddFor, mlOf, formatAmount, helpingsOf,
} from '@padcanvas/shared';

import type { SubstanceCategory } from '@padcanvas/shared';

export const CATEGORY_LABELS: Record<SubstanceCategory, string> = {
  solvent: 'Solvents',
  acid: 'Acids',
  base: 'Bases',
  salt: 'Salts',
  metal: 'Metals',
  nonmetal: 'Non-metals',
  indicator: 'Indicators',
  gas: 'Gases',
};

export const CATEGORY_ORDER: SubstanceCategory[] = ['solvent', 'acid', 'base', 'salt', 'metal', 'nonmetal', 'indicator', 'gas'];

/** Gases are catalogued but never on the shelf — they reach a vessel through a
 *  delivery tube, carried from whatever produced them. Having them as ordinary
 *  substances is what makes "limewater + CO2" a mix the existing engine
 *  already answers, rather than a second kind of question. */
export const GAS_SUBSTANCE_IDS: Record<'hydrogen' | 'carbon_dioxide' | 'sulfur_dioxide' | 'nitrogen_dioxide' | 'nitric_oxide' | 'hydrogen_sulfide' | 'ammonia' | 'hydrogen_chloride' | 'chlorine' | 'other', string> = {
  hydrogen: 'hydrogen_gas',
  carbon_dioxide: 'carbon_dioxide_gas',
  sulfur_dioxide: 'sulfur_dioxide_gas',
  nitrogen_dioxide: 'nitrogen_dioxide_gas',
  nitric_oxide: 'nitric_oxide_gas',
  hydrogen_sulfide: 'hydrogen_sulfide_gas',
  ammonia: 'ammonia_gas',
  hydrogen_chloride: 'hydrogen_chloride_gas',
  chlorine: 'chlorine_gas',
  other: 'unidentified_gas',
};
